import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('fe_token');
      if (!token) {
        setUser(null);
        return;
      }

      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (err) {
      setUser(null);
      localStorage.removeItem('fe_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async credentials => {
    try {
      setError(null);
      const response = await api.post('/auth/login', credentials);
      localStorage.setItem('fe_token', response.data.token);
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const register = async userData => {
    try {
      setError(null);
      const payload = {
        name: userData.fullName || userData.name,
        fullName: userData.fullName,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        password: userData.password,
        password_confirmation: userData.confirmPassword || userData.password_confirmation,
      };

      const response = await api.post('/auth/register', payload);
      return { success: true, message: response.data.message };
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors)?.[0]?.[0]
        : null;
      const errorMsg = firstValidationError || err.response?.data?.message || 'Registration failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      localStorage.removeItem('fe_token');
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null); // Clear user anyway
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
